import {
    ArrowDownTrayIcon,
    ClipboardDocumentCheckIcon,
    EnvelopeIcon,
    ExclamationTriangleIcon,
    PlusIcon,
} from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import ROUTES from '@/ROUTES'

import { api } from '@/services/api.service'

import {
    buildAnalyticsCsv,
    CapacityScaleSection,
    ClientOutcomesKpis,
    CoordinatorPerformanceSection,
    csvFilename,
    OutcomeChartsRow,
    PopulationInsightsSection,
} from './analyticsSections'
import { formatShortDate, ProviderAnalyticsResponse } from './providerAnalytics.types'
import { useProviderSession } from './ProviderSession'

function timeAgo(value: string): string {
    const d = new Date(value)
    if (isNaN(d.valueOf())) return ''
    const minutes = Math.floor((Date.now() - d.valueOf()) / 60000)
    if (minutes < 60) return minutes <= 1 ? 'just now' : `${minutes} minutes ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`
    const days = Math.floor(hours / 24)
    if (days < 14) return days === 1 ? 'yesterday' : `${days} days ago`
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export default function ProviderDashboard() {
    const session = useProviderSession()
    const [data, setData] = useState<ProviderAnalyticsResponse | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        let cancelled = false
        async function load() {
            const response = await api<ProviderAnalyticsResponse>(ROUTES.provider.analytics)
            if (!cancelled && response.status && response.status < 400 && response.data) {
                setData(response.data)
            }
            if (!cancelled) setLoading(false)
        }
        load()
        return () => {
            cancelled = true
        }
    }, [])

    if (loading) {
        return <div className='max-w-6xl mx-auto px-4 py-12 text-center text-sm text-neutral-500'>Loading…</div>
    }

    if (!data) {
        return (
            <div className='max-w-6xl mx-auto px-4 py-12 text-center text-sm text-neutral-500'>
                Could not load your dashboard. Try reloading the page.
            </div>
        )
    }

    const { org, needsAttention, recentActivity } = data
    const seatsRemaining = Math.max(0, org.seatQuota - org.seatsUsed)
    const utilization = org.seatQuota > 0 ? Math.round((org.seatsUsed / org.seatQuota) * 100) : 0

    // Admins and viewers get the analytics-first dashboard; coordinators get the
    // operational dashboard (caseload, what needs attention, quick actions).
    const isCoordinator = session?.role === 'coordinator'
    const isAdmin = session?.role === 'admin'
    const isOversight = !isCoordinator

    function downloadCsv() {
        if (!data) return
        const blob = new Blob([buildAnalyticsCsv(data)], { type: 'text/csv;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = csvFilename(org.displayName)
        a.click()
        URL.revokeObjectURL(url)
    }

    return (
        <div className='max-w-6xl mx-auto px-4 sm:px-6 py-8'>
            {/* Header */}
            <div className='flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8'>
                <div>
                    <p className='text-sm text-neutral-500'>Welcome back,</p>
                    <h1 className='text-2xl sm:text-3xl font-bold text-neutral-900'>{org.displayName}</h1>
                </div>
                <div className='flex items-center gap-2'>
                    {isOversight && (
                        <button
                            onClick={downloadCsv}
                            className='inline-flex items-center gap-2 px-4 py-2 border border-neutral-300 bg-white hover:bg-neutral-50 text-neutral-700 text-sm font-medium rounded-lg transition-colors'
                        >
                            <ArrowDownTrayIcon className='w-4 h-4' />
                            Export
                        </button>
                    )}
                    {isAdmin && (
                        <Link
                            to='/licenses'
                            className='inline-flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition-colors'
                        >
                            <PlusIcon className='w-4 h-4' />
                            Assign a license
                        </Link>
                    )}
                </div>
            </div>

            {/* Seats card (hero) — admin & viewer */}
            {isOversight && (
                <div className='bg-gradient-to-br from-brand-600 to-brand-700 text-white rounded-2xl p-6 mb-8 shadow-sm'>
                    <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-4'>
                        <div>
                            <p className='text-xs uppercase tracking-wider text-white/70'>Seats</p>
                            <div className='flex items-baseline gap-2 mt-1'>
                                <span className='text-4xl font-bold'>{org.seatsUsed}</span>
                                <span className='text-xl text-white/70'>/ {org.seatQuota}</span>
                                <span className='text-sm text-white/80 ml-2'>assigned</span>
                            </div>
                            <p className='text-sm text-white/80 mt-1'>
                                {seatsRemaining} seats remaining. {org.seatsActive} clients have activated their
                                license.
                            </p>
                        </div>
                        <div className='w-full sm:w-64'>
                            <div className='flex justify-between text-xs text-white/80 mb-1'>
                                <span>{utilization}% used</span>
                                <span>{seatsRemaining} free</span>
                            </div>
                            <div className='w-full h-2.5 bg-white/20 rounded-full overflow-hidden'>
                                <div className='h-full bg-white rounded-full' style={{ width: `${utilization}%` }} />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Client outcomes — every role */}
            <ClientOutcomesKpis data={data} />

            {/* Admin & viewer: analytics-first. Charts flow under the outcome KPIs. */}
            {isOversight && (
                <>
                    <OutcomeChartsRow data={data} />
                    <CoordinatorPerformanceSection data={data} />
                    <CapacityScaleSection data={data} />
                </>
            )}

            {/* Coordinator: operational widgets, then the outcome charts at the bottom. */}
            {isCoordinator && (
                <>
                    <NeedsAttention needsAttention={needsAttention} />
                    <div className='grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10'>
                        <RecentActivity recentActivity={recentActivity} />
                        <QuickActions />
                    </div>
                    <OutcomeChartsRow data={data} />
                </>
            )}

            {/* Population insights — every role, at the bottom. */}
            <PopulationInsightsSection data={data} />
        </div>
    )
}

function NeedsAttention({ needsAttention }: { needsAttention: ProviderAnalyticsResponse['needsAttention'] }) {
    const { overdueHomework, stalledInvites } = needsAttention
    return (
        <div className='bg-white rounded-2xl border border-neutral-200 p-6 mb-8'>
            <div className='flex items-center gap-2 mb-4'>
                <ExclamationTriangleIcon className='w-5 h-5 text-amber-500' />
                <h2 className='text-lg font-semibold text-neutral-900'>Needs attention</h2>
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                {/* Overdue homework */}
                <div>
                    <div className='flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-500 mb-3'>
                        <ClipboardDocumentCheckIcon className='w-4 h-4' />
                        Overdue homework ({overdueHomework.length})
                    </div>
                    {overdueHomework.length === 0 ? (
                        <p className='text-sm text-neutral-500'>Nothing overdue. Nice.</p>
                    ) : (
                        <ul className='space-y-2'>
                            {overdueHomework.map((h) => (
                                <li key={h.homeworkId}>
                                    <Link
                                        to={`/clients/${h.licenseId}/homework`}
                                        className='flex items-start justify-between gap-3 p-3 rounded-lg border border-red-100 bg-red-50/40 hover:border-red-300 transition-colors'
                                    >
                                        <div className='min-w-0'>
                                            <div className='text-sm font-medium text-neutral-900 truncate'>
                                                {h.childName ?? 'Client'}
                                            </div>
                                            <div className='text-xs text-neutral-600 truncate'>{h.title}</div>
                                        </div>
                                        <span className='text-xs font-medium text-red-700 whitespace-nowrap'>
                                            Due {formatShortDate(h.dueDate)}
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Stalled invites */}
                <div>
                    <div className='flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-500 mb-3'>
                        <EnvelopeIcon className='w-4 h-4' />
                        Invites awaiting registration 2+ weeks ({stalledInvites.length})
                    </div>
                    {stalledInvites.length === 0 ? (
                        <p className='text-sm text-neutral-500'>No stalled invitations.</p>
                    ) : (
                        <ul className='space-y-2'>
                            {stalledInvites.map((s) => (
                                <li key={s.licenseId}>
                                    <Link
                                        to='/licenses'
                                        className='flex items-start justify-between gap-3 p-3 rounded-lg border border-amber-100 bg-amber-50/40 hover:border-amber-300 transition-colors'
                                    >
                                        <div className='min-w-0'>
                                            <div className='text-sm font-medium text-neutral-900 truncate'>
                                                {s.email}
                                            </div>
                                            <div className='text-xs text-neutral-600'>
                                                Consider resending the invitation
                                            </div>
                                        </div>
                                        <span className='text-xs font-medium text-amber-700 whitespace-nowrap'>
                                            Invited {formatShortDate(s.invitedAt)}
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    )
}

function RecentActivity({ recentActivity }: { recentActivity: ProviderAnalyticsResponse['recentActivity'] }) {
    return (
        <div className='lg:col-span-2 bg-white rounded-2xl border border-neutral-200 p-6'>
            <div className='flex items-center justify-between mb-4'>
                <h2 className='text-lg font-semibold text-neutral-900'>Recent activity</h2>
                <Link to='/clients' className='text-sm text-brand-600 hover:underline'>
                    View all clients
                </Link>
            </div>
            {recentActivity.length === 0 ? (
                <p className='text-sm text-neutral-500'>No activity yet. Invite clients to get started.</p>
            ) : (
                <ul className='space-y-1'>
                    {recentActivity.map((a, i) => {
                        // Homework events drill into the client's homework tab; activation
                        // and note events open the client detail.
                        const to =
                            a.type === 'homework'
                                ? `/clients/${a.licenseId}/homework`
                                : `/clients/${a.licenseId}`
                        const body = (
                            <>
                                <div className='w-2 h-2 rounded-full bg-brand-500 mt-1.5 flex-shrink-0' />
                                <div className='flex-1 min-w-0'>
                                    <p className='text-sm text-neutral-900'>{a.text}</p>
                                    <p className='text-xs text-neutral-500 mt-0.5'>{timeAgo(a.ts)}</p>
                                </div>
                            </>
                        )
                        return (
                            <li key={i} className='border-b border-neutral-100 last:border-0'>
                                {a.licenseId ? (
                                    <Link
                                        to={to}
                                        className='flex items-start gap-3 -mx-2 px-2 py-2 rounded-lg hover:bg-neutral-50 transition-colors'
                                    >
                                        {body}
                                    </Link>
                                ) : (
                                    <div className='flex items-start gap-3 px-0 py-2'>{body}</div>
                                )}
                            </li>
                        )
                    })}
                </ul>
            )}
        </div>
    )
}

function QuickActions() {
    return (
        <div className='bg-white rounded-2xl border border-neutral-200 p-6'>
            <h2 className='text-lg font-semibold text-neutral-900 mb-4'>Quick actions</h2>
            <div className='space-y-2'>
                <QuickAction href='/licenses' label='Assign a license' sub='Send an invitation to a client' />
                <QuickAction
                    href='/clients'
                    label='Review client progress'
                    sub='See engagement signals per client'
                />
                <QuickAction href='/clients' label='Assign homework' sub='Pick a client, then open their homework' />
            </div>
        </div>
    )
}

function QuickAction({ href, label, sub }: { href: string; label: string; sub: string }) {
    return (
        <Link
            to={href}
            className='block p-3 rounded-lg border border-neutral-200 hover:border-brand-400 hover:bg-brand-50/50 transition-all'
        >
            <div className='text-sm font-medium text-neutral-900'>{label}</div>
            <div className='text-xs text-neutral-500'>{sub}</div>
        </Link>
    )
}
